import config from '../config';
import selectedEnvironment from '../env';

const BASEAPI = config[selectedEnvironment].API_URL;
const MASAV_API_URL = `${BASEAPI}/Masav`;

const getCurrentToken = async () => {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(token) : null;
};

const parseContentDispositionFileName = (contentDisposition) => {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
        return match[1].replace(/['"]/g, '').trim();
    }
    return null;
};

const formatMasavPaymentDate = (date) => {
    if (!date) return 'export';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return 'export';
    const day = String(d.getDate()).padStart(2, '0');
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const y = String(d.getFullYear()).slice(-2);
    return `${day}-${m}-${y}`;
};

const buildMasavFileName = (accountantId, paymentDate) =>
    `masav-${accountantId}-${formatMasavPaymentDate(paymentDate)}.txt`;

const postJson = async (path, body, fallbackError) => {
    const token = await getCurrentToken();
    const response = await fetch(`${MASAV_API_URL}/${path}`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || fallbackError);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
};

const masavService = {
    /** nonMasavClients: list active clients NOT enabled for MASAV instead (report-only rows). */
    getMasavBulkDraft: async (accountantId, startDate, endDate, caseIds, monthOnly = false, nonMasavClients = false) => {
        const token = await getCurrentToken();
        const body = {
            accountantId,
            startDate,
            endDate,
            monthOnly: !!monthOnly,
            nonMasavClients: !!nonMasavClients,
        };
        if (Array.isArray(caseIds) && caseIds.length > 0) {
            body.caseIds = caseIds;
        }
        const response = await fetch(`${MASAV_API_URL}/GetMasavBulkDraft`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to load MASAV draft');
        }
        return response.json();
    },

    downloadMasavBulkFile: async (accountantId, startDate, endDate, selectedCaseIds, paymentDate, amountOverrides, monthOnly = false) => {
        const token = await getCurrentToken();
        const body = {
            accountantId,
            startDate,
            endDate,
            selectedCaseIds,
            monthOnly: !!monthOnly,
        };
        // Optional per-case manual amount overrides { [caseId]: amount } replace the computed payable.
        if (amountOverrides && Object.keys(amountOverrides).length > 0) {
            body.amountOverrides = amountOverrides;
        }
        const response = await fetch(`${MASAV_API_URL}/DownloadMasavBulkFile`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to download MASAV file');
        }
        // Preserve raw Windows-1255 bytes — do not decode/re-encode as UTF-8 text.
        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const fileName =
            parseContentDispositionFileName(response.headers.get('content-disposition')) ||
            buildMasavFileName(accountantId, paymentDate ?? endDate);
        return { blob, fileName };
    },

    /** Upload a MASAV file and get back parsed movement rows matched to the accountant's clients. */
    parseMasavFile: async (accountantId, file) => {
        const token = await getCurrentToken();
        const form = new FormData();
        form.append('accountantId', accountantId);
        form.append('file', file);
        const response = await fetch(`${MASAV_API_URL}/ParseMasavFile`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
            },
            body: form,
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to parse MASAV file');
        }
        return response.json();
    },

    getMasavClients: async (accountantId) => {
        const token = await getCurrentToken();
        const response = await fetch(`${MASAV_API_URL}/GetMasavClients`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountantId }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to load MASAV clients');
        }
        return response.json();
    },

    updateMasavClient: async (payload) => {
        const token = await getCurrentToken();
        const response = await fetch(`${MASAV_API_URL}/UpdateMasavClient`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to update MASAV client');
        }
        return response.json();
    },

    getPaymentArrangements: async (accountantId, caseIds) => {
        const token = await getCurrentToken();
        const body = { accountantId };
        if (Array.isArray(caseIds) && caseIds.length > 0) {
            body.caseIds = caseIds;
        }
        const response = await fetch(`${MASAV_API_URL}/GetPaymentArrangements`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to load payment arrangements');
        }
        return response.json();
    },

    upsertPaymentArrangement: async (payload) => {
        const token = await getCurrentToken();
        const response = await fetch(`${MASAV_API_URL}/UpsertPaymentArrangement`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to save payment arrangement');
        }
        return response.json();
    },

    deactivatePaymentArrangement: async (accountantId, caseId) => {
        const token = await getCurrentToken();
        const response = await fetch(`${MASAV_API_URL}/DeactivatePaymentArrangement`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountantId, caseId }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to clear payment arrangement');
        }
        return true;
    },

    /** MASAV clients whose debit day for this month has not arrived yet (caseId = single-client check). */
    getMasavPendingPaymentDay: (accountantId, caseId) =>
        postJson('GetMasavPendingPaymentDay', { accountantId, caseId: caseId || null }, 'Failed to load MASAV payment days'),

    // ── Collection batches (generated files + history) ──

    /** Build the file for the selected clients and save it as a batch. Returns the batch. */
    generateMasavBatch: (accountantId, startDate, endDate, selectedCaseIds, amountOverrides, monthOnly = false) => {
        const body = {
            accountantId,
            startDate,
            endDate,
            selectedCaseIds,
            monthOnly: !!monthOnly,
        };
        if (amountOverrides && Object.keys(amountOverrides).length > 0) {
            body.amountOverrides = amountOverrides;
        }
        return postJson('GenerateMasavBatch', body, 'Failed to generate MASAV file');
    },

    getMasavBatches: (accountantId, monthKey) =>
        postJson('GetMasavBatches', { accountantId, monthKey: monthKey || null }, 'Failed to load MASAV history'),

    downloadMasavBatch: async (accountantId, batch) => {
        const token = await getCurrentToken();
        const response = await fetch(`${MASAV_API_URL}/DownloadMasavBatch`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountantId, batchId: batch.id }),
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to download MASAV file');
        }
        // Preserve raw Windows-1255 bytes — do not decode/re-encode as UTF-8 text.
        const buffer = await response.arrayBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        const fileName =
            parseContentDispositionFileName(response.headers.get('content-disposition')) ||
            batch.fileName ||
            buildMasavFileName(accountantId, batch.paymentDate);
        return { blob, fileName };
    },

    /** items: [{ itemId, amount }] — amount null resets the row to its computed amount. Returns the batch. */
    updateMasavBatchAmounts: (accountantId, batchId, items) =>
        postJson('UpdateMasavBatchAmounts', { accountantId, batchId, items }, 'Failed to update MASAV amounts'),

    cancelMasavBatch: (accountantId, batchId) =>
        postJson('CancelMasavBatch', { accountantId, batchId }, 'Failed to cancel MASAV batch'),

    deleteMasavBatch: (accountantId, batchId) =>
        postJson('DeleteMasavBatch', { accountantId, batchId }, 'Failed to delete MASAV batch'),

    /** Upload the bank's returned MASAV file and match its lines to a batch's rows. */
    parseMatchMasavBatchFile: async (accountantId, batchId, file) => {
        const token = await getCurrentToken();
        const form = new FormData();
        form.append('accountantId', accountantId);
        form.append('batchId', batchId);
        form.append('file', file);
        const response = await fetch(`${MASAV_API_URL}/ParseMatchMasavBatchFile`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
            },
            body: form,
        });
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to parse MASAV file');
        }
        return response.json();
    },

    /** items: [{ caseId, paymentIds: string[], receiptGenerated }] — record applied payments on batch rows. */
    markMasavBatchItemsApplied: (accountantId, batchId, items) =>
        postJson('MarkMasavBatchItemsApplied', { accountantId, batchId, items }, 'Failed to update MASAV batch rows'),

    resetMasavBatchItem: (accountantId, batchId, itemId) =>
        postJson('ResetMasavBatchItem', { accountantId, batchId, itemId }, 'Failed to reset MASAV batch row'),
};

export default masavService;
